import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Sparkles,
  MessageCircle,
  Mail,
  Copy,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const AGGREGATE = "__all__";

function trendVisual(trend) {
  switch (trend) {
    case "up":
      return {
        icon: TrendingUp,
        color: "text-emerald-400",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/20",
      };
    case "down":
      return {
        icon: TrendingDown,
        color: "text-rose-400",
        bg: "bg-rose-500/10",
        border: "border-rose-500/20",
      };
    case "new_activity":
      return {
        icon: Sparkles,
        color: "text-primary",
        bg: "bg-primary/10",
        border: "border-primary/20",
      };
    case "flat":
      return {
        icon: Minus,
        color: "text-muted-foreground",
        bg: "bg-white/[0.04]",
        border: "border-white/10",
      };
    case "no_data":
    default:
      return {
        icon: AlertCircle,
        color: "text-amber-400",
        bg: "bg-amber-500/10",
        border: "border-amber-500/20",
      };
  }
}

function formatPercent(delta_percent, trend) {
  if (delta_percent == null) {
    if (trend === "new_activity") return "Neu";
    if (trend === "no_data") return "—";
    return "—";
  }
  const sign = delta_percent > 0 ? "+" : "";
  return `${sign}${delta_percent}%`;
}

export default function BewertungsschubWidget({ initialData }) {
  const [selected, setSelected] = useState(AGGREGATE);
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const businesses = useMemo(
    () => data?.available_businesses || [],
    [data?.available_businesses]
  );
  const hasMultiple = businesses.length > 1;

  // Pre-select single business if only one exists (so share buttons work immediately).
  useEffect(() => {
    if (!hasMultiple && businesses.length === 1) {
      setSelected(businesses[0].id);
    }
  }, [hasMultiple, businesses]);

  const fetchWidget = async (businessId) => {
    const params =
      businessId && businessId !== AGGREGATE ? { business_id: businessId } : {};
    const { data: d } = await api.get("/portal/bewertungsschub", { params });
    return d;
  };

  useEffect(() => {
    let alive = true;
    if (initialData && selected === AGGREGATE) {
      setData(initialData);
      setLoading(false);
      return () => {};
    }
    setRefreshing(true);
    setError(null);
    fetchWidget(selected === AGGREGATE ? null : selected)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(formatApiError(e));
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const visual = useMemo(() => trendVisual(data?.trend || "no_data"), [data?.trend]);
  const Icon = visual.icon;

  const shareTargetName = data?.business_name;
  const shareTargetUrl = data?.review_url;
  const canShare = Boolean(shareTargetName && shareTargetUrl);

  const waHref = canShare
    ? `https://wa.me/?text=${encodeURIComponent(
        `Vielen Dank für deinen Besuch bei ${shareTargetName}! Wir würden uns sehr über deine Google-Bewertung freuen: ${shareTargetUrl}`
      )}`
    : "#";

  const mailHref = canShare
    ? `mailto:?subject=${encodeURIComponent(
        `Wie war deine Erfahrung bei ${shareTargetName}?`
      )}&body=${encodeURIComponent(
        `Vielen Dank für deinen Besuch bei ${shareTargetName}. Wir freuen uns über dein Feedback auf Google: ${shareTargetUrl}`
      )}`
    : "#";

  const copyLink = async () => {
    if (!canShare) return;
    try {
      await navigator.clipboard.writeText(shareTargetUrl);
      toast.success("Bewertungslink kopiert.");
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  if (loading) {
    return (
      <div
        className="card-surface p-5 flex items-center justify-center min-h-[220px]"
        data-testid="bewertungsschub-widget"
      >
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="card-surface p-5 border-destructive/30"
        data-testid="bewertungsschub-widget"
      >
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      </div>
    );
  }

  if (!businesses.length) {
    return (
      <div className="card-surface p-6 text-center" data-testid="bewertungsschub-widget">
        <p className="label-eyebrow">Bewertungsschub</p>
        <p className="mt-2 text-sm text-muted-foreground">
          Sobald dir eine Firma zugewiesen ist, siehst du hier deinen Wochen-Trend.
        </p>
      </div>
    );
  }

  const isInactive =
    data.scope === "single" && data.business_status && data.business_status !== "active";

  return (
    <div
      className="card-surface p-5 space-y-5"
      data-testid="bewertungsschub-widget"
      data-trend={data.trend}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="label-eyebrow">Bewertungsschub</p>
          <h3 className="font-heading text-lg font-semibold mt-1">
            Diese Woche vs. Vorwoche
          </h3>
          <p className="text-[11px] text-muted-foreground mt-1">
            {data.current_period_start} – {data.current_period_end}
            {"  ·  "}
            vs. {data.previous_period_start} – {data.previous_period_end}
          </p>
        </div>

        {hasMultiple ? (
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger
              className="w-[210px] bg-background/40 border-white/[0.08]"
              data-testid="bewertungsschub-business-select"
            >
              <SelectValue placeholder="Firma auswählen" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value={AGGREGATE} data-testid="bewertungsschub-option-all">
                Alle Firmen (aggregiert)
              </SelectItem>
              {businesses.map((b) => (
                <SelectItem key={b.id} value={b.id} data-testid={`bewertungsschub-option-${b.id}`}>
                  {b.business_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>

      {isInactive ? (
        <div
          className="text-xs bg-amber-500/10 border border-amber-500/20 text-amber-200 rounded-lg px-3 py-2"
          data-testid="bewertungsschub-inactive"
        >
          Diese Firma ist zurzeit deaktiviert — Scans werden nicht mehr gezählt.
        </div>
      ) : null}

      {/* Numbers */}
      <div className="grid grid-cols-[1fr,auto,1fr] items-end gap-4">
        <div>
          <div className="label-eyebrow">Diese Woche</div>
          <div
            className="mt-1 text-4xl font-heading font-bold tabular-nums"
            data-testid="bewertungsschub-current"
          >
            {data.current_period_count}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Scans</div>
        </div>

        <div
          className={`self-center rounded-full px-3 py-1.5 border ${visual.bg} ${visual.border} ${visual.color} inline-flex items-center gap-1.5 text-sm font-medium`}
          data-testid="bewertungsschub-delta"
        >
          <Icon className="w-3.5 h-3.5" />
          {formatPercent(data.delta_percent, data.trend)}
        </div>

        <div className="text-right">
          <div className="label-eyebrow">Vorwoche</div>
          <div
            className="mt-1 text-2xl font-heading font-semibold tabular-nums text-muted-foreground"
            data-testid="bewertungsschub-previous"
          >
            {data.previous_period_count}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Scans</div>
        </div>
      </div>

      {/* Status + CTA */}
      <div>
        <p
          className={`text-sm ${visual.color}`}
          data-testid="bewertungsschub-status-text"
        >
          {data.status_text}
        </p>
        <p
          className="text-xs text-muted-foreground mt-1"
          data-testid="bewertungsschub-cta-text"
        >
          {data.cta_text}
        </p>
      </div>

      {/* Share actions */}
      <div className="pt-2 border-t border-white/[0.05]">
        <p className="label-eyebrow mb-2">Bewertungslink teilen</p>
        {canShare ? (
          <div className="grid grid-cols-3 gap-2">
            <ShareButton
              href={waHref}
              icon={MessageCircle}
              label="WhatsApp"
              testId="bewertungsschub-share-whatsapp"
              external
            />
            <ShareButton
              href={mailHref}
              icon={Mail}
              label="E-Mail"
              testId="bewertungsschub-share-email"
            />
            <button
              onClick={copyLink}
              data-testid="bewertungsschub-share-copy"
              className="rounded-full h-10 px-3 flex items-center justify-center gap-1.5 bg-white/[0.03] border border-white/[0.08] text-sm hover:bg-white/[0.06] transition-colors"
            >
              <Copy className="w-4 h-4" /> Link
            </button>
          </div>
        ) : (
          <p
            className="text-xs text-muted-foreground"
            data-testid="bewertungsschub-share-hint"
          >
            Wähle oben eine bestimmte Firma aus, um den Bewertungslink zu teilen.
          </p>
        )}
      </div>

      {refreshing ? (
        <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" /> Wird aktualisiert…
        </div>
      ) : null}
    </div>
  );
}

function ShareButton({ href, icon: Icon, label, testId, external }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className="rounded-full h-10 px-3 flex items-center justify-center gap-1.5 bg-primary/10 border border-primary/20 text-primary text-sm hover:bg-primary/15 transition-colors"
    >
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
}
